import React, { useEffect, useState, useMemo, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useDispatch, useSelector } from "react-redux";
import ProgressBar from "../../../components/ProgressBar";
import ImageMapper from "react-img-mapper";
import {
  clearTableSelectionApiStatus,
  submitSelections,
} from "../../../redux/processor/actions";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";
import { Box, IconButton, Tooltip } from "@mui/material";
import { CircularProgress } from "@mui/material";
import "./preMapData.scss";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const TabelSelections = React.forwardRef(
  (
    {
      imageData,
      selectionType,
      imageMapperWidth,
      imageMapperHeight,
      onClicked,
      pageRotateAngle,
      onSubmitCrop,
      pdfAndPageDetails,
      currentPageNo,
      currentFileNo,
      onCropDragStateChange,
      ...props
    },
    ref,
  ) => {
    const defaultCropArea = {
      unit: "px",
      x: 116,
      y: 44,
      width: 58,
      height: 13,
    };

    const dispatch = useDispatch();
    const tableSelectionApiStatus = useSelector(
      (state) => state?.pro?.tableSelectionApiStatus,
    );
    let extractedTableData = useSelector(
      (state) => state?.pro?.extractedTableData,
    );

    const [crop, setCrop] = useState(null);
    const [selectedArea, setSelectedArea] = useState({});
    const [showSubmitButton, setShowSubmitButton] = useState(false);
    const [showCropScreen, setShowCropScreen] = useState(true);
    const [imageLoadedInDiv, setImageLoadedInDiv] = useState(false);
    const [savedBBox, setSavedBBox] = useState(null);

    // ─── FIX (clarity on zoom): force the rendered image onto its own GPU ───
    // compositing layer. PrismaZoom's double-click zoom animates a CSS
    // `transform: scale()` on an ancestor. Browsers sometimes promote that
    // animated element to a compositing layer and rasterize a SNAPSHOT of
    // it at the pre-zoom on-screen size, then just stretch that snapshot
    // for the duration of (and sometimes after) the animation — instead of
    // continuously re-sampling the image's actual decoded bitmap. This is
    // why clarity drops specifically "after zoom" even though the
    // underlying image already has plenty of real resolution (we verified
    // that earlier — native res for unrotated, renderScale-boosted canvas
    // for rotated). Explicitly promoting the <img> itself to its own layer
    // (translateZ + backfaceVisibility + willChange) tells the browser to
    // keep sampling from the real bitmap as the texture, rather than a
    // smaller cached snapshot taken at the wrapper's pre-zoom size.
    const gpuLayerHintStyle = {
      transform: "translateZ(0)",
      WebkitBackfaceVisibility: "hidden",
      backfaceVisibility: "hidden",
      willChange: "transform",
    };

    // ─── FIX (bbox drag vs pan conflict): safety-net reset ──────────────────
    // onCropDragStateChange(true)/(false) is meant to tell the parent to
    // disable PrismaZoom's panning while the user is actively dragging a
    // crop box (parent flips `allowPan` accordingly — see ImageViewer). The
    // normal path for turning it back ON is ReactCrop's onDragEnd. But if
    // the user releases the mouse/finger somewhere ReactCrop doesn't see as
    // a clean drag-end (e.g. drags out past the image edge, or the browser
    // swallows the mouseup), panning could stay stuck disabled. This global
    // listener is a safety net that always re-enables panning on ANY
    // mouseup/touchend, regardless of where it happens.
    useEffect(() => {
      const resetDragState = () => onCropDragStateChange?.(false);
      window.addEventListener("mouseup", resetDragState);
      window.addEventListener("touchend", resetDragState);
      return () => {
        window.removeEventListener("mouseup", resetDragState);
        window.removeEventListener("touchend", resetDragState);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── FIX (blurry / squeezed text): true aspect-ratio guard ───────────────
    // The parent may pass an imageMapperWidth/imageMapperHeight box whose
    // aspect ratio does NOT match the real image's aspect ratio (e.g. parent
    // passes 600x768 — a portrait 0.78:1 box — while the actual image is
    // 3506x2480, a landscape 1.41:1 scan). If we draw the source image
    // directly into that mismatched box, the image gets stretched
    // non-uniformly: width and height get scaled by DIFFERENT factors. That
    // non-uniform stretch is what makes scanned text look mushy/unreadable
    // — it's not just a downscale blur, characters are literally distorted
    // taller/thinner or shorter/wider than they should be.
    //
    // safeBaseDims recomputes the correct "original/unrotated" display box
    // by preserving the real image's aspect ratio, fitting it inside the
    // imageMapperWidth x imageMapperHeight box (letterboxed, not stretched).
    // Falls back to the raw props if imageData width/height aren't available
    // yet, so behavior is unchanged until we actually know the real ratio.
    const safeBaseDims = useMemo(() => {
      if (!imageData?.width || !imageData?.height) {
        return { width: imageMapperWidth, height: imageMapperHeight };
      }
      const srcRatio = imageData.width / imageData.height;
      const boxRatio = imageMapperWidth / imageMapperHeight;
      return srcRatio > boxRatio
        ? {
            width: imageMapperWidth,
            height: Math.round(imageMapperWidth / srcRatio),
          }
        : {
            width: Math.round(imageMapperHeight * srcRatio),
            height: imageMapperHeight,
          };
    }, [
      imageData?.width,
      imageData?.height,
      imageMapperWidth,
      imageMapperHeight,
    ]);

    // ─── FIX (blurry text ON ZOOM, rotated pages only): render canvas at ────
    // higher pixel density. PrismaZoom zooms via CSS `transform: scale()` —
    // it doesn't add pixel detail, it just stretches whatever pixels already
    // exist. When a page IS rotated we still have to pre-rotate via canvas
    // (see big comment below for why), and that canvas was previously only
    // ever rendered at the small DISPLAY size, throwing away all the extra
    // resolution from the source. renderScale tells that canvas to allocate
    // a bigger pixel buffer (closer to native resolution) instead, capped by
    // MAX_CANVAS_DIMENSION for memory/perf. NOTE: as of the native-resolution
    // fix further below, this only matters anymore when angle !== 0 — at 0°
    // we skip canvas entirely and use the source image natively, same as the
    // validator's ImageViewer.
    const MAX_CANVAS_DIMENSION = 2400;

    const renderScale = useMemo(() => {
      if (!imageData?.width || !imageData?.height) return 1;
      // How much extra resolution is actually available from the source
      const idealScale = Math.min(
        imageData.width / safeBaseDims.width,
        imageData.height / safeBaseDims.height,
      );
      // Don't render bigger than this cap, regardless of source resolution
      const maxAllowedScale = Math.min(
        MAX_CANVAS_DIMENSION / safeBaseDims.width,
        MAX_CANVAS_DIMENSION / safeBaseDims.height,
      );
      return Math.max(1, Math.min(idealScale, maxAllowedScale));
    }, [
      imageData?.width,
      imageData?.height,
      safeBaseDims.width,
      safeBaseDims.height,
    ]);

    // ─── FIX (Issue 1 & 2): canvas-based image pre-rotation ──────────────────
    // CSS `transform: rotate()` only rotates the VISUAL appearance — it does
    // NOT change the element's layout box or how ReactCrop reads pointer
    // coordinates. ReactCrop always computes crop x/y against the image's
    // unrotated layout rect, so when the image is visually spun via CSS,
    // drag positions never match what the user sees. Wrapping the rotation
    // on an outer div does not fix this either, since ReactCrop still reads
    // its own bounding box.
    //
    // The only robust fix FOR ROTATED PAGES: physically rotate the IMAGE
    // PIXELS using a <canvas>, then feed that ALREADY-ROTATED image (as a
    // data URL) into ReactCrop with NO css transform at all. ReactCrop then
    // operates on a normal, unrotated image — drag coordinates always match
    // visually.
    //
    // ─── FIX (native resolution when unrotated, "validator-style") ─────────
    // When pageRotateAngle is 0°, there's nothing to fix — ReactCrop's drag
    // math works fine directly against a plain, un-rotated <img>. In that
    // case we now skip the canvas pipeline ENTIRELY and just point straight
    // at the original imageData.imageUrl, sized only via CSS (safeBaseDims)
    // — exactly the same approach the validator's ImageViewer/ImageMapper
    // uses. The browser keeps the image's full native pixel data in memory
    // regardless of the small CSS display size, so when PrismaZoom zooms in
    // afterwards there's real detail to draw from — no re-rasterization, no
    // toDataURL, no quality loss, and it's faster too since we skip the
    // canvas draw + encode for the common (unrotated) case entirely.
    // We only fall back to the canvas-rotation + renderScale approach below
    // when the page is ACTUALLY rotated (90/180/270), since that's the only
    // case ReactCrop's drag coordinates need a pre-rotated image to stay
    // correct.
    const [rotatedImageUrl, setRotatedImageUrl] = useState(null);
    const [rotatedDims, setRotatedDims] = useState({
      width: imageMapperWidth,
      height: imageMapperHeight,
    });
    const sourceImgRef = useRef(null);

    useEffect(() => {
      if (!imageData?.imageUrl) {
        setRotatedImageUrl(null);
        return;
      }

      const angle = ((pageRotateAngle % 360) + 360) % 360; // normalize 0-359

      // ─── FIX (native resolution when unrotated) ────────────────────────
      // No rotation needed → skip canvas entirely, use the source image
      // directly at native resolution, same as the validator component.
      if (angle === 0) {
        setRotatedImageUrl(imageData.imageUrl);
        setRotatedDims({
          width: safeBaseDims.width,
          height: safeBaseDims.height,
        });
        return;
      }

      // Page IS rotated → fall back to canvas pre-rotation (required for
      // ReactCrop's drag math — see comment block above), with renderScale
      // applied so it still stays sharp when zoomed.
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = imageData.imageUrl;

      img.onload = () => {
        sourceImgRef.current = img;

        // For 90/270, displayed width/height swap; for 0/180 they stay same.
        const swap = angle === 90 || angle === 270;
        const baseW = safeBaseDims.width;
        const baseH = safeBaseDims.height;
        const canvasW = swap ? baseH : baseW;
        const canvasH = swap ? baseW : baseH;

        const canvas = document.createElement("canvas");
        // Allocate the canvas's pixel buffer at renderScale× the logical/
        // CSS size — see renderScale comment block above.
        canvas.width = canvasW * renderScale;
        canvas.height = canvasH * renderScale;
        const ctx = canvas.getContext("2d");

        ctx.save();
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        // Scale the context up-front so every coordinate below (still
        // written in terms of canvasW/canvasH/baseW/baseH, i.e. the
        // ORIGINAL logical size) gets stretched onto the higher-resolution
        // physical canvas we just allocated above. None of the rotation/
        // translate math needs to change.
        ctx.scale(renderScale, renderScale);
        // Move to center, rotate, draw image centered, then restore
        ctx.translate(canvasW / 2, canvasH / 2);
        ctx.rotate((angle * Math.PI) / 180);

        // When rotated 90/270 the image's own w/h (pre-rotation) is baseW/baseH
        // drawn centered at origin
        ctx.drawImage(img, -baseW / 2, -baseH / 2, baseW, baseH);
        ctx.restore();

        // rotatedDims stays the LOGICAL/CSS size — this is what ReactCrop,
        // the bbox overlay, and all the coordinate-mapping functions below
        // use, so nothing downstream needs to know about renderScale at all.
        setRotatedDims({ width: canvasW, height: canvasH });
        setRotatedImageUrl(canvas.toDataURL());
      };

      img.onerror = () => {
        // Fallback: if canvas rotation fails (e.g. CORS), show image as-is
        // without rotation rather than breaking the UI entirely.
        setRotatedImageUrl(imageData.imageUrl);
        setRotatedDims({ width: safeBaseDims.width, height: safeBaseDims.height });
      };
    }, [
      imageData?.imageUrl,
      pageRotateAngle,
      imageMapperWidth,
      imageMapperHeight,
      imageData?.width,
      imageData?.height,
      safeBaseDims.width,
      safeBaseDims.height,
      renderScale,
    ]);

    // ─── Scale factors ────────────────────────────────────────────────────────
    // Now based on rotatedDims since that's the actual canvas the crop tool
    // operates on after rotation.
    const scaleX = useMemo(
      () => rotatedDims.width / (imageData?.width || 1),
      [rotatedDims.width, imageData?.width],
    );
    const scaleY = useMemo(
      () => rotatedDims.height / (imageData?.height || 1),
      [rotatedDims.height, imageData?.height],
    );

    // ─── Map ORIGINAL (unrotated) backend coords → ROTATED display space ────
    // Backend coordinates in pdfAndPageDetails are always in the ORIGINAL,
    // unrotated image's pixel space (same space the parent's onSubmitCrop
    // scaleX/scaleY math assumes). But the image shown to the user is now
    // pre-rotated via canvas. So before placing the overlay box, we must
    // forward-rotate the original coords into rotated display space — this
    // is the mirror/inverse of mapRotatedCropToOriginal used on submit.
    const mapOriginalToRotatedDisplay = (origBox) => {
      // origBox is { x, y, width, height } already scaled to imageMapperWidth/Height
      // (i.e. in ORIGINAL unrotated display pixels, NOT raw PDF pixels)
      const angle = ((pageRotateAngle % 360) + 360) % 360;
      const { x, y, width, height } = origBox;

      const oW = safeBaseDims.width;
      const oH = safeBaseDims.height;

      let rotX, rotY, rotWidth, rotHeight;

      switch (angle) {
        case 90:
          // forward rotate 90° CW: point (x,y) in original -> (oH-(y+height), x) in rotated
          rotX = oH - (y + height);
          rotY = x;
          rotWidth = height;
          rotHeight = width;
          break;
        case 180:
          rotX = oW - (x + width);
          rotY = oH - (y + height);
          rotWidth = width;
          rotHeight = height;
          break;
        case 270:
          rotX = y;
          rotY = oW - (x + width);
          rotWidth = height;
          rotHeight = width;
          break;
        default: // 0
          rotX = x;
          rotY = y;
          rotWidth = width;
          rotHeight = height;
      }

      return { x: rotX, y: rotY, width: rotWidth, height: rotHeight };
    };

    useEffect(() => {
      if (!pdfAndPageDetails?.coordinates?.length) {
        setSavedBBox(null);
        return;
      }

      if (
        pdfAndPageDetails?.pageNo !== currentPageNo ||
        pdfAndPageDetails?.fileNo !== currentFileNo
      ) {
        setSavedBBox(null);
        return;
      }

      // Step 1: backend raw coords -> original (unrotated) display pixels
      const normalizedOriginal = pdfAndPageDetails.coordinates.map((bbox) => ({
        x: bbox[0] * (safeBaseDims.width / (imageData?.width || 1)),
        y: bbox[1] * (safeBaseDims.height / (imageData?.height || 1)),
        width:
          (bbox[2] - bbox[0]) * (safeBaseDims.width / (imageData?.width || 1)),
        height:
          (bbox[3] - bbox[1]) * (safeBaseDims.height / (imageData?.height || 1)),
      }));

      // Step 2: rotate those original-space boxes into current rotated
      // display space so they land on the correct spot after rotation
      const normalizedRotated = normalizedOriginal.map(
        mapOriginalToRotatedDisplay,
      );

      setSavedBBox(normalizedRotated);
    }, [
      pdfAndPageDetails?.coordinates,
      pdfAndPageDetails?.pageNo,
      pdfAndPageDetails?.fileNo,
      currentPageNo,
      currentFileNo,
      pageRotateAngle, // ← re-run whenever rotation changes
      imageMapperWidth,
      imageMapperHeight,
      imageData?.width,
      imageData?.height,
      safeBaseDims.width,
      safeBaseDims.height,
    ]);

    // ─── selectionType changes ────────────────────────────────────────────────
    useEffect(() => {
      if (selectionType === "table") {
        setCrop({ unit: "px", x: 116, y: 44, width: 58, height: 13 });
        setShowSubmitButton(false);
      } else if (selectionType === "page") {
        const selectedAreaForFullPage = {
          unit: "px",
          x: 5,
          y: 5,
          height: rotatedDims.height - 10,
          width: rotatedDims.width - 10,
        };
        setCrop(selectedAreaForFullPage);
        setSelectedArea({ ...selectedAreaForFullPage });
        setShowSubmitButton(true);
      }
      setShowCropScreen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionType]);

    useEffect(() => {
      dispatch(clearTableSelectionApiStatus());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (tableSelectionApiStatus?.apiSuccess === true) {
        setShowCropScreen(false);
      }
    }, [tableSelectionApiStatus]);

    // ─── Ref exposed to parent ────────────────────────────────────────────────
    const onChangePdforPageWhenTableSelectin = () => {
      setShowCropScreen(true);
      setCrop(defaultCropArea);
    };

    React.useImperativeHandle(ref, () => ({
      onChangePdforPageWhenTableSelectin,
    }));

    const onChangeCrop = (c) => {
      setCrop(c);
      if (showSubmitButton && c.height === 0 && c.width === 0) {
        setShowSubmitButton(false);
      }
    };

    const onCropCompleted = (c) => {
      if (c.height && c.width) {
        setSelectedArea(c);
      }
    };

    // ─── FIX (Issue 1 & 2 cont.): un-rotate crop coords before sending up ────
    // Since ReactCrop now operates on the ROTATED canvas image (when rotated)
    // or the native image directly (when not), the crop rectangle it
    // returns is in that same display space. Before calling onSubmitCrop
    // (which the parent uses to map back to original PDF coordinates), we
    // must transform the rectangle back into the ORIGINAL (unrotated)
    // image's coordinate space — this is the inverse of the rotation we
    // applied on canvas (a no-op when angle is 0).
    const mapRotatedCropToOriginal = (rotatedCrop) => {
      const angle = ((pageRotateAngle % 360) + 360) % 360;
      const { x, y, width, height } = rotatedCrop;

      // rotatedDims is the canvas/display size the crop was drawn on
      const rW = rotatedDims.width;
      const rH = rotatedDims.height;

      // Original (unrotated, displayed) dimensions are now aspect-correct
      const oW = safeBaseDims.width;
      const oH = safeBaseDims.height;

      let origX, origY, origWidth, origHeight;

      switch (angle) {
        case 90:
          // rotated 90° CW: original top-left maps from rotated bottom-left
          origX = y;
          origY = rW - (x + width);
          origWidth = height;
          origHeight = width;
          break;
        case 180:
          origX = rW - (x + width);
          origY = rH - (y + height);
          origWidth = width;
          origHeight = height;
          break;
        case 270:
          origX = rH - (y + height);
          origY = x;
          origWidth = height;
          origHeight = width;
          break;
        default: // 0
          origX = x;
          origY = y;
          origWidth = width;
          origHeight = height;
      }

      return { x: origX, y: origY, width: origWidth, height: origHeight };
    };

    // Shared drag handlers used by BOTH ReactCrop instances
    const handleDragStart = () => {
      setSavedBBox(null);
      onCropDragStateChange?.(true);
    };

    const handleDragEnd = () => {
      onCropDragStateChange?.(false);
      if (crop?.width > 0 && crop?.height > 0) {
        setSavedBBox([
          { x: crop.x, y: crop.y, width: crop.width, height: crop.height },
        ]);
        const originalSpaceCrop = mapRotatedCropToOriginal(crop);
        onSubmitCrop({ selectedArea: originalSpaceCrop });
        setCrop(null);
      }
    };

    const handleSubmit = () => {
      const originalSpaceArea = mapRotatedCropToOriginal(selectedArea);

      if (selectionType === "page") {
        dispatch(
          submitSelections({
            name: imageData?.name,
            pageNo: imageData?.pageNo,
            imageUrl: imageData?.imageUrl,
            extract_table_only: false,
            pageRotateAngle: pageRotateAngle,
          }),
        );
      } else if (selectionType === "table") {
        dispatch(
          submitSelections({
            name: imageData?.name,
            pageNo: imageData?.pageNo,
            imageUrl: imageData?.imageUrl,
            extract_table_only: true,
            pageRotateAngle: pageRotateAngle,
            coords: [
              originalSpaceArea?.x,
              originalSpaceArea?.y,
              originalSpaceArea?.width,
              originalSpaceArea?.height,
            ],
          }),
        );
      } else {
        onSubmitCrop({ selectedArea: originalSpaceArea });
      }
      setCrop(null);
    };

    const handleCancel = () => {
      setCrop(null);
      setShowSubmitButton(false);
    };

    // ─── ImageMapper click ────────────────────────────────────────────────────
    const clicked = (area, index, e) => {
      const dataToUpdate = [...extractedTableData];
      dataToUpdate[index] = {
        ...extractedTableData[index],
        preFillColor: "#ff00002e",
        fillColor: "#ff00002e",
      };
      extractedTableData = dataToUpdate;
      onClicked(area, index, e);
    };

    const handleImageLoad = () => setImageLoadedInDiv(true);

    const buttonPosition = {
      top:
        selectionType === "page"
          ? 10
          : (crop?.y ?? 0) + (crop?.height ?? 0) + 15,
      left: (crop?.x ?? 0) + (crop?.width ?? 0) - 20,
    };

    // ─── Shared bbox overlay renderer ─────────────────────────────────────────
    const BBoxOverlay = () =>
      savedBBox?.map((bbox, index) => (
        <div
          key={index}
          style={{
            position: "absolute",
            left: `${bbox.x}px`,
            top: `${bbox.y}px`,
            width: `${bbox.width}px`,
            height: `${bbox.height}px`,
            border: "2px solid #2302022e",
            backgroundColor: "#ff00002e",
            boxSizing: "border-box",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        />
      ));

    // ─── FIX (Issue 3): disable crop-drag in "page" mode only ────────────────
    const isCropDisabled = selectionType === "page";

    // Show loading state while canvas rotation is in progress
    const isRotatedImageReady = !!rotatedImageUrl;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
      <>
        {showCropScreen ? (
          <div className="p-relative">
            <div
              id="container"
              style={{
                cursor: selectionType === "drawBbox" ? "crosshair" : "grab",
                position: "relative",
              }}
            >
              {!isRotatedImageReady ? (
                <Box
                  className="premap-loader-container"
                  style={{ width: safeBaseDims.width, height: safeBaseDims.height }}
                >
                  <CircularProgress />
                  <span className="premap-loader-style">Loading...</span>
                </Box>
              ) : selectionType === "drawBbox" ? (
                // ── drawBbox mode — pre-rotated when angle≠0, native when 0°
                <div
                  style={{
                    position: "relative",
                    width: rotatedDims.width,
                    height: rotatedDims.height,
                  }}
                  // ─── FIX (bbox drag vs pan conflict): tell the parent to
                  // disable PrismaZoom panning as early as possible — on the
                  // very first mousedown/touchstart over the crop area —
                  // rather than waiting for ReactCrop's onDragStart, which
                  // can fire a tick later and lose the race against
                  // PrismaZoom's own pan listener once you're zoomed in.
                  onMouseDown={() => onCropDragStateChange?.(true)}
                  onTouchStart={() => onCropDragStateChange?.(true)}
                >
                  <ReactCrop
                    crop={crop}
                    onChange={onChangeCrop}
                    onComplete={onCropCompleted}
                    maxWidth={rotatedDims.width}
                    maxHeight={rotatedDims.height}
                    style={{ display: "block" }}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <img
                      src={rotatedImageUrl}
                      alt="TableImage"
                      style={{
                        width: rotatedDims.width,
                        height: rotatedDims.height,
                        display: "block",
                        ...gpuLayerHintStyle,
                      }}
                    />
                  </ReactCrop>
                  <BBoxOverlay />
                </div>
              ) : (
                // ── table / page / coords mode — pre-rotated when angle≠0,
                // native resolution straight from source when angle=0 ──────
                <div
                  style={{
                    position: "relative",
                    width: rotatedDims.width,
                    height: rotatedDims.height,
                  }}
                  // ─── FIX (bbox drag vs pan conflict): same early-disable
                  // safety net as the drawBbox branch above.
                  onMouseDown={() => onCropDragStateChange?.(true)}
                  onTouchStart={() => onCropDragStateChange?.(true)}
                >
                  <ReactCrop
                    crop={crop}
                    onChange={onChangeCrop}
                    onComplete={onCropCompleted}
                    style={{ display: "block" }}
                    disabled={isCropDisabled}
                    onDragStart={handleDragStart}
                    onDragEnd={() => {
                      onCropDragStateChange?.(false);
                      if (crop?.width > 0 && crop?.height > 0) {
                        setSavedBBox([
                          {
                            x: crop.x,
                            y: crop.y,
                            width: crop.width,
                            height: crop.height,
                          },
                        ]);
                        const originalSpaceCrop =
                          mapRotatedCropToOriginal(crop);
                        onSubmitCrop({ selectedArea: originalSpaceCrop });
                        setCrop(null);
                      }
                    }}
                  >
                    <img
                      alt="bbox-draw"
                      src={rotatedImageUrl}
                      style={{
                        width: rotatedDims.width,
                        height: rotatedDims.height,
                        display: "block",
                        // NO css transform here — at angle 0 this is the
                        // native source image; at angle≠0, pixels are
                        // already rotated via canvas above
                        ...gpuLayerHintStyle,
                      }}
                    />
                  </ReactCrop>
                  <BBoxOverlay />
                </div>
              )}
            </div>

            {/* Submit / Cancel buttons */}
            {showSubmitButton && crop?.width > 0 && crop?.height > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: buttonPosition.top,
                  left: buttonPosition.left,
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  gap: "6px",
                  zIndex: 10,
                }}
              >
                <Tooltip title="Submit">
                  <IconButton
                    size="small"
                    onClick={handleSubmit}
                    style={{
                      background: "#4CD426",
                      color: "#fff",
                      width: 20,
                      height: 20,
                    }}
                  >
                    <CheckIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    onClick={handleCancel}
                    style={{
                      background: "#f44336",
                      color: "#fff",
                      width: 20,
                      height: 20,
                    }}
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </div>
            )}

            {tableSelectionApiStatus?.loading && <ProgressBar />}
          </div>
        ) : (
          // ── ImageMapper (post-extraction) — unchanged, still uses CSS
          // rotation since ImageMapper isn't a drag-to-draw tool, just
          // click-based area selection where this mismatch doesn't apply.
          <div
            id="container"
            style={{
              transform: `rotate(${pageRotateAngle}deg)`,
              transition: "transform 150ms ease",
              cursor: selectionType === "drawBbox" ? "crosshair" : "grab",
              left: [
                90, 270, 450, 630, 810, 990, 1170, 1350, 1530, 1710, 1890,
              ].includes(pageRotateAngle)
                ? "14%"
                : 0,
              position: "relative",
            }}
          >
            <img
              alt="Map_Image"
              src={imageData.imageUrl}
              onLoad={handleImageLoad}
              className="d-none"
            />
            {imageLoadedInDiv ? (
              <ImageMapper
                src={imageData.imageUrl}
                map={{ name: imageData?.name, areas: [...extractedTableData] }}
                width={imageMapperWidth}
                height={imageMapperHeight}
                onClick={(area, index, e) => clicked(area, index, e)}
                imgWidth={imageMapperWidth}
                natural={true}
                responsive={true}
                parentWidth={imageMapperWidth}
              />
            ) : (
              <Box className="premap-loader-container">
                <CircularProgress />
                <span className="premap-loader-style">Loading...</span>
              </Box>
            )}
          </div>
        )}

        {/* Snackbars */}
        {tableSelectionApiStatus?.apiSuccess === true && (
          <CustomizedSnackbars
            severity="success"
            state={tableSelectionApiStatus?.apiSuccess}
            text={tableSelectionApiStatus?.message}
            handleClose={() => dispatch(clearTableSelectionApiStatus())}
          />
        )}
        {tableSelectionApiStatus?.apiError === true && (
          <CustomizedSnackbars
            severity="error"
            state={tableSelectionApiStatus?.apiError}
            text={tableSelectionApiStatus?.message}
            handleClose={() => dispatch(clearTableSelectionApiStatus())}
          />
        )}
      </>
    );
  },
);

export default TabelSelections;